/**
 * Centralized React Query configuration for consistent caching across the app.
 * Tiered staleTime values reduce database queries based on data change frequency.
 */

export const QUERY_CONFIG = {
  // Tier 1: Rarely changes (24 hours) - categories, static content
  STATIC: { 
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 48 * 60 * 60 * 1000,
  },
  
  // Tier 2: Infrequently changes (1 hour) - user profile, settings
  PROFILE: { 
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  },
  
  // Tier 3: Moderate updates (15 minutes) - assessments, saved data
  ASSESSMENT: { 
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  },
  
  // Tier 4: More dynamic (5 minutes) - social content, threads
  SOCIAL: { 
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  },
  
  // Tier 5: Near real-time (1 minute) - notifications, activity
  REALTIME: { 
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  },
};

/**
 * Query key factories for consistent key generation
 */
export const queryKeys = {
  profile: {
    all: ['profile'] as const,
    user: (userId: string) => ['profile', userId] as const,
    current: () => ['profile', 'current'] as const,
  },
  privacy: {
    all: ['privacy'] as const,
    user: (userId: string) => ['privacy', userId] as const,
  },
  assessments: {
    all: ['assessments'] as const,
    user: (userId: string) => ['assessments', userId] as const,
    single: (id: string) => ['assessments', 'detail', id] as const,
    latest: (userId: string, variant: string) => ['assessments', 'latest', userId, variant] as const,
  },
  community: {
    categories: () => ['forum-categories'] as const,
    threads: (filters?: unknown) => ['community-threads', filters] as const,
    thread: (id: string) => ['community-thread', id] as const,
    replies: (threadId: string) => ['thread-replies', threadId] as const,
  },
  liker: {
    preferences: (userId: string) => ['liker', 'preferences', userId] as const,
    inference: (userId: string) => ['liker', 'inference', userId] as const,
    stats: (userId: string) => ['liker', 'stats', userId] as const,
  },
  quiz: {
    results: (userId: string) => ['quiz-results', userId] as const,
    count: (userId: string) => ['quiz-count', userId] as const,
  },
  insights: {
    unified: (userId: string) => ['insights', 'unified', userId] as const,
    crossFeature: (userId: string) => ['insights', 'cross-feature', userId] as const,
    ai: (assessmentId: string) => ['insights', 'ai', assessmentId] as const,
  },
  xp: {
    total: (userId: string) => ['xp-total', userId] as const,
    gifts: (userId: string) => ['xp-gifts', 'total', userId] as const,
  },
};
